import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import api from '../api/axios';
import AuthLayout from '../components/AuthLayout';
import '../styles/auth.css';

export default function Register() {
  const navigate = useNavigate();
  const [step, setStep] = useState(1);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [entityId, setEntityId] = useState('');
  const [entityRowId, setEntityRowId] = useState('');

  const [orgForm, setOrgForm] = useState({
    name: '',
    phone: '',
    email: '',
  });

  const [userForm, setUserForm] = useState({
    firstName: '',
    middleName: '',
    lastName: '',
    phone: '',
    email: '',
    password: '',
    confirmPassword: '',
  });

  function handleOrgChange(e) {
    setOrgForm({ ...orgForm, [e.target.name]: e.target.value });
    setError('');
  }

  function handleUserChange(e) {
    setUserForm({ ...userForm, [e.target.name]: e.target.value });
    setError('');
  }

  async function handleOrgSubmit(e) {
    e.preventDefault();
    setLoading(true);
    try {
      const { data } = await api.post('/api/auth/register/entity', orgForm);
      setEntityId(data.entityId);
      setEntityRowId(data.entityRowId);
      setUserForm({ ...userForm, phone: orgForm.phone, email: orgForm.email });
      setStep(2);
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to create organization.');
    } finally {
      setLoading(false);
    }
  }

  async function handleUserSubmit(e) {
    e.preventDefault();
    if (userForm.password !== userForm.confirmPassword) {
      setError('Passwords do not match.');
      return;
    }
    if (userForm.password.length < 6) {
      setError('Password must be at least 6 characters.');
      return;
    }
    setLoading(true);
    try {
      const { data } = await api.post('/api/auth/register/user', {
        entityRowId,
        firstName: userForm.firstName,
        middleName: userForm.middleName,
        lastName: userForm.lastName,
        phone: userForm.phone,
        email: userForm.email,
        password: userForm.password,
      });
      localStorage.setItem('token', data.token);
      localStorage.setItem('user', JSON.stringify(data.user));
      navigate('/dashboard');
    } catch (err) {
      setError(err.response?.data?.message || 'Registration failed. Please try again.');
    } finally {
      setLoading(false);
    }
  }

  return (
    <AuthLayout>
      <div className="auth-card">
        <div className="auth-logo">
          <img src="/mustechlogo.png" alt="Mustech" className="logo-image" />
          <h1 className="auth-brand">Mustech Bulk SMS</h1>
        </div>

        {step === 1 ? (
          <>
            <h2 className="auth-title">Create your organization</h2>
            <p className="auth-subtitle">Step 1 of 2: Set up your business account</p>

            {error && <div className="auth-error">{error}</div>}

            <form onSubmit={handleOrgSubmit} className="auth-form">
              <div className="form-group">
                <label htmlFor="name">Organization name</label>
                <input
                  id="name"
                  type="text"
                  name="name"
                  placeholder="Your Company Ltd."
                  value={orgForm.name}
                  onChange={handleOrgChange}
                  required
                />
              </div>

              <div className="form-group">
                <label htmlFor="email">Organization email</label>
                <input
                  id="email"
                  type="email"
                  name="email"
                  placeholder="contact@company.com"
                  value={orgForm.email}
                  onChange={handleOrgChange}
                  required
                />
              </div>

              <div className="form-group">
                <label htmlFor="phone">Phone number</label>
                <input
                  id="phone"
                  type="tel"
                  name="phone"
                  placeholder="+254712345678"
                  value={orgForm.phone}
                  onChange={handleOrgChange}
                  required
                />
              </div>

              <button type="submit" className="btn-primary" disabled={loading}>
                {loading ? <span className="spinner" /> : 'Continue'}
              </button>
            </form>
          </>
        ) : (
          <>
            <h2 className="auth-title">Create your account</h2>
            <p className="auth-subtitle">Step 2 of 2: Set up your admin account</p>

            {error && <div className="auth-error">{error}</div>}

            <form onSubmit={handleUserSubmit} className="auth-form">
              <div className="form-row">
                <div className="form-group">
                  <label htmlFor="firstName">First name</label>
                  <input
                    id="firstName"
                    type="text"
                    name="firstName"
                    placeholder="John"
                    value={userForm.firstName}
                    onChange={handleUserChange}
                    required
                  />
                </div>

                <div className="form-group">
                  <label htmlFor="middleName">Middle name</label>
                  <input
                    id="middleName"
                    type="text"
                    name="middleName"
                    placeholder="Michael"
                    value={userForm.middleName}
                    onChange={handleUserChange}
                  />
                </div>
              </div>

              <div className="form-group">
                <label htmlFor="lastName">Last name</label>
                <input
                  id="lastName"
                  type="text"
                  name="lastName"
                  placeholder="Doe"
                  value={userForm.lastName}
                  onChange={handleUserChange}
                  required
                />
              </div>

              <div className="form-group">
                <label htmlFor="userEmail">Email address</label>
                <input
                  id="userEmail"
                  type="email"
                  name="email"
                  placeholder="john.doe@example.com"
                  value={userForm.email}
                  onChange={handleUserChange}
                  required
                />
              </div>

              <div className="form-group">
                <label htmlFor="userPhone">Phone number</label>
                <input
                  id="userPhone"
                  type="tel"
                  name="phone"
                  placeholder="+254712345678"
                  value={userForm.phone}
                  onChange={handleUserChange}
                  required
                />
              </div>

              <div className="form-row">
                <div className="form-group">
                  <label htmlFor="password">Password</label>
                  <input
                    id="password"
                    type="password"
                    name="password"
                    placeholder="••••••••"
                    value={userForm.password}
                    onChange={handleUserChange}
                    required
                  />
                </div>

                <div className="form-group">
                  <label htmlFor="confirmPassword">Confirm password</label>
                  <input
                    id="confirmPassword"
                    type="password"
                    name="confirmPassword"
                    placeholder="••••••••"
                    value={userForm.confirmPassword}
                    onChange={handleUserChange}
                    required
                  />
                </div>
              </div>

              <p className="terms-note">
                By signing up, you agree to our{' '}
                <a href="#terms">Terms of Service</a> and{' '}
                <a href="#privacy">Privacy Policy</a>.
              </p>

              <div className="btn-group">
                <button
                  type="button"
                  className="btn-secondary"
                  onClick={() => setStep(1)}
                >
                  Back
                </button>
                <button type="submit" className="btn-primary" disabled={loading}>
                  {loading ? <span className="spinner" /> : 'Complete Registration'}
                </button>
              </div>
            </form>
          </>
        )}

        <p className="auth-footer">
          Already have an account?{' '}
          <Link to="/login">Sign in</Link>
        </p>
      </div>
    </AuthLayout>
  );
}
